(() => {
  const root = document.getElementById('matagi-all-reviews');
  const nav = root?.querySelector('[data-review-pages]');
  if (!nav) return;
  const status = nav.querySelector('[data-review-status]');
  const buttons = nav.querySelector('[data-review-buttons]');
  const size = 10;
  const state = new WeakMap();
  let busy = false;
  let queued = false;

  const lists = () => [...root.querySelectorAll('.jdgm-all-reviews__body, .jdgm-shop-reviews__body')];
  const activeList = () => lists().find(list => getComputedStyle(list).display !== 'none');
  const reviews = list => [...list.children].filter(item => item.classList.contains('jdgm-rev'));
  const nativeData = list => window.jdgm?.$ ? window.jdgm.$(list).data() : {};
  const total = list => {
    const key = list.classList.contains('jdgm-shop-reviews__body') ? 'shop' : 'product';
    const data = nativeData(list);
    if (Number.isFinite(Number(data.totalCount))) return Number(data.totalCount);
    const count = root.querySelector(`.jdgm-subtab__name[data-tabname="${key}-reviews"] .jdgm-subtab__count`);
    const header = root.querySelector('.jdgm-all-reviews__header');
    return Math.max(reviews(list).length, parseInt(count?.textContent || header?.getAttribute(`data-number-of-${key}-reviews`) || '0', 10) || 0);
  };
  const pageState = list => {
    const items = reviews(list);
    let current = state.get(list);
    // A new first review means Judge.me has replaced the results after filtering.
    if (!current || current.first !== items[0]) {
      current = { page: 1, first: items[0] };
      state.set(list, current);
    }
    return current;
  };

  function render() {
    for (const list of lists()) {
      const current = pageState(list);
      reviews(list).forEach((review, index) => {
        review.toggleAttribute('data-mw-review-hidden', index < (current.page - 1) * size || index >= current.page * size);
      });
    }
    const list = activeList();
    if (!list) { nav.hidden = true; return; }
    const current = pageState(list);
    const count = total(list);
    const pages = Math.max(1, Math.ceil(count / size));
    const loaded = reviews(list).length;
    const signature = `${current.page}:${count}:${loaded}:${busy}`;
    if (!root.classList.contains('mw-review-pages-ready')) root.classList.add('mw-review-pages-ready');
    nav.hidden = count === 0;
    if (nav.dataset.state === signature) return;
    nav.dataset.state = signature;
    status.textContent = busy ? nav.dataset.loading : nav.dataset.range
      .replace('{start}', String(Math.min((current.page - 1) * size + 1, count)))
      .replace('{end}', String(Math.min(current.page * size, loaded, count)))
      .replace('{total}', String(count));
    buttons.replaceChildren();
    function button(label, target, disabled, isCurrent = false) {
      const element = document.createElement('button');
      element.type = 'button';
      element.textContent = label;
      element.dataset.page = target;
      element.disabled = disabled || busy;
      if (isCurrent) element.setAttribute('aria-current', 'page');
      if (/^\d+$/.test(label)) element.setAttribute('aria-label', `${nav.dataset.pageLabel} ${label}`);
      buttons.append(element);
    }
    button('‹', current.page - 1, current.page === 1);
    buttons.lastChild.setAttribute('aria-label', nav.dataset.previous);
    const visible = new Set([1, pages, current.page - 1, current.page, current.page + 1]);
    if (pages <= 7) for (let i = 1; i <= pages; i++) visible.add(i);
    let previous = 0;
    [...visible].filter(n => n >= 1 && n <= pages).sort((a, b) => a - b).forEach(number => {
      if (previous && number > previous + 1) {
        const gap = document.createElement('span');
        gap.textContent = '…';
        gap.setAttribute('aria-hidden', 'true');
        buttons.append(gap);
      }
      button(String(number), number, false, number === current.page);
      previous = number;
    });
    button('›', current.page + 1, current.page === pages);
    buttons.lastChild.setAttribute('aria-label', nav.dataset.next);
  }

  const pause = () => new Promise(resolve => setTimeout(resolve, 100));
  async function goToPage(target) {
    if (busy) return;
    const list = activeList();
    if (!list) return;
    const current = pageState(list);
    busy = true;
    render();
    try {
      const needed = Math.min(target * size, total(list));
      const deadline = Date.now() + 15000;
      while (reviews(list).length < needed) {
        if (Date.now() > deadline || activeList() !== list || state.get(list) !== current) throw new Error('Results changed or timed out');
        if (nativeData(list).isLoading) { await pause(); continue; }
        const loader = root.querySelector('.jdgm-all-reviews-page__load-more');
        if (!loader || nativeData(list).allReviewsLoaded) throw new Error('No more reviews available');
        const before = reviews(list).length;
        loader.click();
        do { await pause(); } while (nativeData(list).isLoading && Date.now() < deadline);
        if (reviews(list).length <= before) throw new Error('Reviews could not be loaded');
      }
      if (activeList() !== list || state.get(list) !== current) throw new Error('Results changed');
      current.page = target;
      busy = false;
      render();
      buttons.querySelector('[aria-current="page"]')?.focus({ preventScroll: true });
      root.querySelector('.jdgm-subtab')?.scrollIntoView({ block: 'start', behavior: 'instant' });
      if (!root.querySelector('.jdgm-subtab')) list.scrollIntoView({ block: 'start', behavior: 'instant' });
    } catch {
      busy = false;
      render();
      status.textContent = nav.dataset.error;
    }
  }
  buttons.addEventListener('click', event => {
    const button = event.target.closest('button[data-page]');
    if (button && !button.disabled) goToPage(Number(button.dataset.page));
  });
  const observer = new MutationObserver(records => {
    if (records.every(record => nav.contains(record.target))) return;
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; render(); });
  });
  observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
  render();
})();
